import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import PublicNavigation from '../../../shared/components/layout/PublicNavigation';
import Navigation from '../../../components/layout/Navigation';
import Footer from '../../../shared/components/layout/Footer';
import ImageViewer from '../../../shared/components/ui/ImageViewer';

/**
 * About page showing detailed information about Vester.
 * This is the content that was previously on the landing page.
 * Shows conditional navigation based on authentication status.
 */
const AboutPage: React.FC = () => {
  const { user } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  
  const images = [
    { src: '/vester_portfolio1.png', alt: 'Vester Portfolio Preview' },
    { src: '/vester_chat1.png', alt: 'Vester Chat Preview' },
  ];

  const handleImageClick = (index: number) => {
    setViewerIndex(index);
    setIsViewerOpen(true);
  };

  const handleViewerNavigate = (index: number) => {
    setViewerIndex(index);
    setCurrentSlide(index); // Sync with carousel
  };

  return (
    <div className="bg-white">
      {user ? <Navigation /> : <PublicNavigation />}
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-green-50 to-green-100 opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <img
                src="/Vester-logo-lrg-clr.png"
                alt="Vester"
                className="h-24 sm:h-32 md:h-40 w-auto"
              />
            </div>
            <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
              <span className="block text-black">Get rich together</span>
            </h1>
            <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
              An antisocial network to track investments, learn together, and share strategies with close friends and family—all while keeping your dollar values private.
            </p>
            <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
              <div className="rounded-md shadow">
                <Link
                  to={user ? "/portfolio" : "/beta-request"}
                  className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 md:py-4 md:text-lg md:px-10"
                >
                  {user ? "Go to Portfolio" : "Sign up for the Beta"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              About Vester
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              <strong>Vester is a new kind of investing app: an “antisocial network”.</strong>
              <br />
              Forget broadcasting your money moves to everyone you know. With Vester, you share only
              with the people who really matter: close friends and family.
              <br /><br />

              Create tight-knit groups where you can share your entire portfolio, just parts of it,
              or simply swap ideas in chat. Your dollar amounts always stay private — friends see
              performance in percentages, not balances.
              <br /><br />

              See which investments are driving your best friend's returns, or discover what your most
              financially savvy friend is buying next. Why keep all that knowledge to yourself? With Vester,
              you can share what's working so you and your friends can <strong>get rich together</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-2">
            {/* Feature 1 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-green-600 mb-4">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Private Portfolio Tracking</h3>
              <p className="mt-2 text-base text-gray-500">
                Track everything from stocks and ETFs to high-yield savings accounts—all in one place.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-green-600 mb-4">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Trusted Friend Circles</h3>
              <p className="mt-2 text-base text-gray-500">
                Share strategies with friends using a 4-tier privacy model. No public profiles, ever.
              </p>
            </div>

            {/* Feature 3 - Alerts & Watchlists (Not yet implemented) */}
            {/* <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-green-600 mb-4">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Alerts & Watchlists</h3>
              <p className="mt-2 text-base text-gray-500">
                Get notified when your favorite stocks move or your allocation changes.
              </p>
            </div> */}

            {/* Feature 4 - Learn Together (Not yet implemented) */}
            {/* <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-green-600 mb-4">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Learn Together</h3>
              <p className="mt-2 text-base text-gray-500">
                Access curated financial content tailored to young professionals getting started.
              </p>
            </div> */}
          </div>
        </div>
      </div>

      {/* App Preview Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-lg overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-600 opacity-10"></div>
            <div className="relative p-8">
              <div className="rounded-lg shadow-lg overflow-hidden relative">
                {/* Carousel Container */}
                <div className="relative w-full overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{
                      transform: `translateX(-${currentSlide * 100}%)`,
                    }}
                  >
                    {images.map((image, index) => (
                      <div
                        key={index}
                        className="w-full flex-shrink-0 cursor-pointer"
                        onClick={() => handleImageClick(index)}
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="w-full h-auto transition-opacity hover:opacity-90"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Carousel Indicators */}
                <div className="flex justify-center items-center mt-4 space-x-2">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`transition-all duration-300 rounded-full ${
                        index === currentSlide
                          ? 'w-3 h-3 bg-green-600'
                          : 'w-3 h-3 bg-gray-400 hover:bg-gray-500'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Vester Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-12">
            Why Vester?
          </h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* Card 1 */}
            <div className="text-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-600 text-white mx-auto">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                No public feeds or followers
              </h3>
              <p className="mt-2 text-base text-gray-500">
                Vester is built for small, invite-only circles, not internet clout. There are no
                public leaderboards, no follower counts, and nothing for strangers to scroll through. Here, you are encouraged to be antisocial.
              </p>
            </div>

            {/* Card 2 */}
            <div className="text-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-600 text-white mx-auto">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Friend tiers, not “everyone”
              </h3>
              <p className="mt-2 text-base text-gray-500">
                Use a 4-tier sharing model (Gold, Silver, Bronze, and Basic) to choose exactly what each friend sees. 
                Each tier has different permissions ranging from full visibility of all of your trades, to just chat-only.
              </p>
            </div>

            {/* Card 3 */}
            <div className="text-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-600 text-white mx-auto">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Dollar amounts stay private
              </h3>
              <p className="mt-2 text-base text-gray-500">
                Vester's data model is privacy-first: friends see performance in percentages,
                never dollar values. You can even hide specific accounts or transactions, while still sharing the
                insights that really matter.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Full-Screen Image Viewer */}
      <ImageViewer
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        images={images}
        currentIndex={viewerIndex}
        onNavigate={handleViewerNavigate}
      />
    </div>
  );
};

export default AboutPage;

